import React, { useState, useEffect, useRef } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import api from "../../utils/api";
import { Upload, Copy, Trash2, CheckCircle, ImageIcon, FileText, X } from "lucide-react";

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(filename) {
  return /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(filename);
}

export default function MediaPage() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef();

  const loadFiles = () => {
    api.get("/upload/list").then(r => setFiles(r.data)).catch(() => {});
  };

  useEffect(() => { loadFiles(); }, []);

  const handleUpload = async (fileList) => {
    const file = fileList[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError("Datei zu groß (max. 5 MB)"); return; }
    setError("");
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post("/upload/image", form, { headers: { "Content-Type": "multipart/form-data" } });
      loadFiles();
    } catch (err) {
      setError(err.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const deleteFile = async (filename) => {
    if (!window.confirm(`"${filename}" wirklich löschen?`)) return;
    await api.delete(`/upload/file/${filename}`);
    loadFiles();
  };

  return (
    <AdminLayout>
      <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-black text-white font-heading">Mediathek</h1>
        <span className="text-zinc-500 text-sm">{files.length} Dateien</span>
      </div>

      {/* Upload-Zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        data-testid="media-upload-zone"
        className={`mb-6 border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
          dragOver ? "border-rose-500 bg-rose-950/20" : "border-zinc-700 hover:border-zinc-500 bg-zinc-900/50"
        }`}
      >
        <input ref={inputRef} type="file" className="hidden"
          accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,application/pdf,text/plain"
          onChange={e => handleUpload(e.target.files)} />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-zinc-400">
            <div className="w-4 h-4 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
            Wird hochgeladen…
          </div>
        ) : (
          <>
            <Upload size={24} className="mx-auto text-zinc-600 mb-2" />
            <p className="text-zinc-400 text-sm font-medium">Datei hierher ziehen oder klicken</p>
            <p className="text-zinc-600 text-xs mt-1">JPG, PNG, WebP, GIF, SVG, PDF · max. 5 MB</p>
          </>
        )}
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 bg-red-900/20 border border-red-800/30 text-red-400 text-sm px-4 py-2.5 rounded-lg">
          <X size={13} /> {error}
        </div>
      )}

      {/* Datei-Grid */}
      {files.length === 0 ? (
        <p className="text-zinc-600 text-sm text-center py-12">Noch keine Dateien hochgeladen.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {files.map(f => (
            <div key={f.filename} className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden group"
              data-testid={`media-file-${f.filename}`}>
              {/* Vorschau */}
              <div className="aspect-square bg-zinc-800 flex items-center justify-center overflow-hidden">
                {isImage(f.filename) ? (
                  <img src={f.url} alt={f.filename} className="w-full h-full object-cover" />
                ) : (
                  <FileText size={32} className="text-zinc-600" />
                )}
              </div>
              {/* Info */}
              <div className="p-2">
                <p className="text-zinc-400 text-xs truncate mb-1" title={f.filename}>{f.filename}</p>
                <p className="text-zinc-600 text-xs mb-2">{formatSize(f.size)}</p>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => copyUrl(f.url)}
                    data-testid={`copy-url-${f.filename}`}
                    title="URL kopieren"
                    className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors text-xs text-zinc-400 hover:text-white"
                  >
                    {copiedUrl === f.url ? <><CheckCircle size={11} className="text-green-400" /> Kopiert</> : <><Copy size={11} /> URL</>}
                  </button>
                  <button
                    onClick={() => deleteFile(f.filename)}
                    title="Löschen"
                    className="px-2 py-1.5 rounded-lg bg-zinc-800 hover:bg-red-900/40 transition-colors text-zinc-600 hover:text-red-400"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </AdminLayout>
  );
}
